import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Me, Solicitation, Stats, Tag } from '../lib/api';
import { daysUntil, fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { Empty, Kpi, PageHead, Panel, Pill } from '../components/ui';
import { BandPill, DecisionButtons, DecisionPill, ScoreBadge } from '../components/matches';
import { AlertList } from '../components/alerts';

const MODULES: { name: string; status: 'live' | 'next' | 'planned' | 'later'; ref: string }[] = [
  { name: 'Solicitation monitoring: SAM.gov ingest twice daily, version history, notes', status: 'live', ref: 'V2 §2.X.3' },
  { name: 'Technology tagging of every notice (NAICS, PSC, title rules)', status: 'live', ref: 'V2 §2.X.3' },
  { name: 'Client profiling: overview, strategic priorities, opportunity criteria, NTN intelligence; ASU first', status: 'live', ref: 'V2 §2.X.1; CPD §1, §5' },
  { name: 'Client ↔ solicitation fit scoring with Relevant / Monitor / Pursue / Not relevant decisions', status: 'live', ref: 'V2 §2.X.3, §2.X.4; CPD §2' },
  { name: 'Client dashboard: new opportunities since last review, priority opportunities, program updates, strategic initiatives', status: 'live', ref: 'CPD §3' },
  { name: 'Opportunity relevance assessments (why, implication, gaps, next steps), Claude-written or rules-based, editable by NTN', status: 'live', ref: 'CPD §4' },
  { name: 'In-app alerts: strong new matches, deadlines, amendments, watch rules', status: 'live', ref: 'V2 §2.X.2, §2.X.3' },
  { name: 'Workspace admin: members, roles, invitations', status: 'live', ref: 'NTN §6 (RBAC)' },
  { name: 'Budget and appropriations: R-1 / P-1 exhibits versioned by FY and stage, marks, what changed, client relevance', status: 'live', ref: 'V2 §2.X.2' },
  { name: 'Mission needs: capability gaps before a solicitation, linked forward to notices and budget lines', status: 'live', ref: 'V3 §2.X.1' },
  { name: 'Ecosystem capability map: organizations and claims, mission-to-capability matching, team formation and gaps', status: 'live', ref: 'V3 §2.X.2, §2.X.3, §2.X.4' },
  { name: 'Events with per-client recommendations and 90/60/30-day deadline alerts', status: 'live', ref: 'V3 §2.X.5, §2.X.6' },
  { name: 'Digital Analyst over the platform data, with citations', status: 'next', ref: 'NTN §3' },
  { name: 'Weekly intelligence newsletter (after the Digital Analyst)', status: 'planned', ref: 'NTN §5' },
  { name: 'Call-for-projects management (SHIELD-operated calls)', status: 'later', ref: 'V3 §2.X.7' },
  { name: 'Transition tracking and ecosystem portfolio dashboard', status: 'later', ref: 'V3 §2.X.8, §2.X.9' },
];
const STATUS_PILL: Record<string, { kind: 'ok' | 'warn' | 'info' | 'muted'; label: string }> = {
  live: { kind: 'ok', label: 'Live' }, next: { kind: 'warn', label: 'Next' }, planned: { kind: 'info', label: 'Planned' }, later: { kind: 'muted', label: 'Later phase' },
};

export function Command({ me }: { me: Me }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [closing, setClosing] = useState<Solicitation[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api<Stats>('/api/stats').then(setStats).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(() => {
    load();
    api<{ items: Solicitation[] }>('/api/solicitations?due_within_days=14&sort=due_asc&limit=8').then((r) => setClosing(r.items)).catch(() => setClosing([]));
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
  }, [load]);

  const li = stats?.last_ingest ?? null;
  const canDecide = me.role !== 'viewer';
  return (
    <>
      <PageHead
        title="Command Center"
        sub="What is open, what fits, what is closing, what changed"
        actions={<span className="updated">{li ? `Last SAM.gov pull ${fmtDateTime(li.started_at)} · ${li.fetched.toLocaleString()} notices · ${li.status}` : 'No ingest yet'}</span>}
      />
      {err && <div className="error-text">{err}</div>}
      <div className="kpi-row">
        <Kpi label="Open solicitations" value={stats?.solicitations_active.toLocaleString() ?? '…'} />
        <Kpi label="Closing within 14 days" value={stats?.due_14d.toLocaleString() ?? '…'} />
        <Kpi label="Client profiles" value={stats?.clients ?? '…'} />
        <Kpi label="Pending matches ≥ 50" value={stats?.pending_matches ?? '…'} />
        <Kpi label="Pursuing" value={stats?.pursuing ?? '…'} />
        <Kpi label="Open alerts" value={stats?.alerts_open ?? '…'} />
      </div>

      <div className="grid grid-2">
        <Panel title="Strongest undecided matches" action={<a className="link" href="#/clients">Client profiles →</a>}>
          {stats && stats.top_matches.length === 0 ? (
            <Empty>No pending matches. Add technology tags and NAICS codes to a profile, or wait for the next ingest.</Empty>
          ) : (
            <table className="data-table">
              <thead><tr><th>Fit</th><th>Client · opportunity</th><th>Expires</th><th>Decision</th></tr></thead>
              <tbody>
                {(stats?.top_matches ?? []).map((m) => {
                  const days = daysUntil(m.response_due_at);
                  return (
                    <tr key={m.id} className="clickable" onClick={() => { location.hash = `/solicitations/id/${m.solicitation_id}`; }}>
                      <td><ScoreBadge score={m.score} /></td>
                      <td>
                        <div className="muted sm"><a className="link" href={`#/clients/${m.client_id}`} onClick={(e) => e.stopPropagation()}>{m.client_name}</a> · {titleCase(m.notice_type)} {m.band && <BandPill band={m.band} total={m.priority_total} />}</div>
                        <strong>{m.title}</strong>
                        {m.rationale?.tags?.length ? <div className="muted sm">{m.rationale.tags.map((t) => tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id).join(', ')}{m.rationale.agency?.matched ? ` · ${titleCase(m.rationale.agency.matched.toLowerCase())}` : ''}</div> : null}
                      </td>
                      <td>{m.response_due_at ? <>{fmtDate(m.response_due_at)}{days !== null && days >= 0 && <div className={`sm ${days <= 3 ? 'neg' : 'muted'}`}>{days === 0 ? 'today' : `${days} days`}</div>}</> : <span className="muted">—</span>}</td>
                      <td onClick={(e) => e.stopPropagation()}>{canDecide ? <DecisionButtons matchId={m.id} decision={m.decision} onChanged={load} /> : <DecisionPill decision={m.decision} />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title={`Intelligence alerts${stats ? ` · ${stats.alerts_open} open` : ''}`} action={stats && stats.alerts_open > 8 ? <span className="muted sm">showing latest 8</span> : undefined}>
          <AlertList items={stats?.alerts ?? []} canAck={canDecide} onChanged={load} />
        </Panel>
      </div>

      <div className="grid grid-2">
        <Panel title="Closing soon" action={<a className="link" href="#/solicitations">View all →</a>}>
          {closing.length ? (
            <table className="data-table">
              <thead><tr><th>Solicitation</th><th>Type</th><th>Due</th></tr></thead>
              <tbody>
                {closing.map((s) => {
                  const d = daysUntil(s.response_due_at);
                  return (
                    <tr key={s.id} className="clickable" onClick={() => { location.hash = `/solicitations/id/${s.id}`; }}>
                      <td><strong>{s.title}</strong><div className="muted sm">{s.issuing_org_path?.split('.').slice(-1)[0] ?? s.solicitation_number ?? ''}</div></td>
                      <td>{titleCase(s.notice_type)}</td>
                      <td><span className={d !== null && d <= 3 ? 'neg' : ''}>{fmtDate(s.response_due_at)}</span>{d !== null && <div className="muted sm">{d === 0 ? 'today' : `${d} day${d === 1 ? '' : 's'}`}</div>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty>No solicitations closing in the next 14 days.</Empty>
          )}
        </Panel>

        <Panel title="Recent workspace activity">
          {stats?.activity.length ? stats.activity.map((a, i) => (
            <div className="alert" key={i}>
              <span className={`alert-tag ${a.action === 'insert' ? 'notable' : 'material'}`}>{a.action}</span>
              <div className="alert-body">
                <div>{a.label ?? '(no label)'} <span className="muted sm">· {titleCase(a.table_name)}</span></div>
                <div className="muted sm">{fmtDateTime(a.changed_at)}</div>
              </div>
            </div>
          )) : (
            <Empty>No changes recorded yet. Every insert and update in this workspace lands here, permanently.</Empty>
          )}
        </Panel>
      </div>

      <Panel title="Build status against the NewCo scope documents">
        {MODULES.map((m) => (
          <div className="status-line" key={m.name}>
            <Pill kind={STATUS_PILL[m.status].kind}>{STATUS_PILL[m.status].label}</Pill>
            <span>{m.name}</span>
            <span className="muted sm" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>{m.ref}</span>
          </div>
        ))}
      </Panel>
    </>
  );
}
